const changeHistory = {
  dialog: {
    title: "Änderungshistorie",
  },
  columns: {
    timestamp: "Zeitpunkt",
    change: "Änderung",
    field: "Feld",
    detail: "Detail",
    oldValue: "Alter Wert",
    newValue: "Neuer Wert",
    user: "Bearbeiter:in",
  },
  entry: {
    fieldChanged: "Feld geändert",
    entryAdded: "Eintrag hinzugefügt",
    entryRemoved: "Eintrag entfernt",
    created: "Erstellt",
    createdText: "{{entity}} angelegt",
  },
  actor: {
    fswPortal: "FSW-Portal",
    portal: "Kund:in (Portal)",
    system: "SYSTEM",
    unknown: "Unbekannt",
  },
};

export default changeHistory;

// Lookup maps for backend-generated identifiers (field keys and entity types of the
// change-history endpoints). The keys arrive at runtime from the API, so typed t() cannot
// express them — they are accessed as plain maps. Unmapped keys degrade to the raw key.
// The German wording mirrors the corresponding form labels.
//
// A field of a classified entry may carry a per-type label, keyed "<fieldKey>.<type>" and falling
// back to the plain field key: the value of a contact detail is an "E-Mail" or a "Nummer" depending
// on the entry's type, where a bare "Wert" would say nothing.
export const changeHistoryFieldLabels: Record<string, string> = {
  "client.comment": "Bemerkung",
  "client.salutation": "Anrede",
  "client.title": "Titel",
  "client.firstName": "Vorname",
  "client.lastName": "Familienname",
  "client.formerLastName": "Früherer Familienname",
  "client.street": "Straße",
  "client.houseNumber": "Hausnummer/Stiege/Tür",
  "client.zipCode": "Plz",
  "client.city": "Ort",
  "client.country": "Land",
  "client.birthday": "Geburtsdatum",
  "client.insuranceNumber": "Versicherungsnummer",
  "client.employmentStatus": "Beruf oder Beschäftigung",
  "client.placeOfBirth": "Geburtsort",
  "client.initialRegistrationDate": "Erstmeldedatum",
  "client.contactSource": "Kontaktquelle",
  "client.qualification": "Qualifikation",
  "client.nationality": "Staatsbürgerschaft",
  "client.maritalStatus": "Familienstand",
  "client.livingSituation": "Wohnsituation",
  "client.mediaInquiry": "Medienanfrage",
  "client.privacyConsentGivenUtc": "Datenschutz-Zustimmung",
  "client.note": "Notiz",
  "client.estimatedDebt": "Geschätzte Verschuldung",
  "client.estimatedNumberOfCreditors": "Geschätzte Anzahl Gläubiger",
  "client.status": "Status",
  "client.assignedCounselorId": "Bearbeiter:in",
  "caseNote.type": "Art AV",
  "caseNote.date": "Datum",
  "caseNote.time": "Zeit",
  "caseNote.assignedCounselorId": "Bearbeiter:in",
  "caseNote.location": "Ort",
  "caseNote.content": "Bericht",
  "caseNote.result": "Ergebnis",
  "caseNote.appointmentStatus": "Terminstatus",
  "assetInventory.causesDescription": "Ursachen der Verschuldung",
  "assetInventory.phoneNumber": "Telefonnummer",
  "assetInventory.hasOwnAccount": "Eigenes Konto vorhanden",
  "assetInventory.hasFurtherAccount": "Weiteres Konto vorhanden",
  "assetInventory.hasSelfEmploymentIncome": "Ansprüche aus selbstständiger Erwerbstätigkeit",
  "assetInventory.selfEmploymentDescription": "Beschreibung der Selbstständigkeit",
  "assetInventory.selfEmploymentMonthlyIncome": "Monatliche Einkünfte aus Selbstständigkeit",
  "assetInventory.hasIllnessExtraCosts": "Mehrauslagen wegen Krankheit (Diätkosten)",
  "assetInventory.illnessExtraCostAmount": "Monatliche Mehrauslagen aufgrund von Krankheit",
  "assetInventory.paysCostsAlone": "Laufende Kosten alleine getragen",
  "assetInventory.costSharingExplanation": "Mit wem die Kosten geteilt werden",
  "assetInventory.expenseRemarks": "Bemerkungen zu den Ausgaben",
  "assetInventory.hasOwnClaims": "Forderungen gegenüber anderen vorhanden",
  "assetInventory.hasDomesticRealEstate": "Grundstück/Wohnung/Haus/Baurecht in Österreich vorhanden",
  "assetInventory.domesticRealEstateDetails": "Angaben zur Immobilie in Österreich",
  "assetInventory.hasForeignRealEstate": "Grundstück/Haus im Ausland vorhanden",
  "assetInventory.foreignRealEstateDetails": "Angaben zur Immobilie im Ausland",
  "assetInventory.hasBuildingSavingsContracts": "Bausparverträge vorhanden",
  "assetInventory.hasSavingsAccounts": "Sparbücher/-konten vorhanden",
  "assetInventory.hasLifeInsurances": "Lebensversicherungen vorhanden",
  "assetInventory.hasPensionInsurances": "Pensionsvorsorgeversicherungen vorhanden",
  "assetInventory.hasSecuritiesDepots": "Wertpapierdepots vorhanden",
  "assetInventory.hasSafeDeposit": "Safe/Bankschließfach gemietet",
  "assetInventory.safeDepositDetails": "Angaben zum Bankschließfach",
  "assetInventory.hasOtherCashAssets": "Sonstiges Bar- oder Bankvermögen vorhanden",
  "assetInventory.isSavedForProceeding": "Für den Privatkonkurs angespart",
  "assetInventory.savedForProceedingAmount": "Höhe des angesparten Betrages",
  "assetInventory.savedForProceedingDate": "Stichtag des angesparten Betrages",
  "assetInventory.otherCashAssetsDetails": "Angaben zum sonstigen Bar- und/oder Bankvermögen",
  "assetInventory.hasValuables": "Schmuck/Wertsachen vorhanden",
  "assetInventory.valuablesDetails": "Schmuck/Wertsachen",
  "assetInventory.hasFurnishings": "Einrichtungsgegenstände vorhanden",
  "assetInventory.furnishingsDetails": "Einrichtungsgegenstände",
  "assetInventory.hasAppliances": "Elektro-/Haushaltsgeräte vorhanden",
  "assetInventory.appliancesDetails": "Elektro-/Haushaltsgeräte",
  "assetInventory.hasEquipment": "Gebrauchsgegenstände vorhanden",
  "assetInventory.equipmentDetails": "Gebrauchsgegenstände",
  "assetInventory.hasVehicles": "Kraftfahrzeug vorhanden",
  "assetInventory.vehiclesDetails": "Kraftfahrzeuge",
  "assetInventory.hasBuildingsOnForeignLand": "Bauten auf fremden Grundstücken vorhanden",
  "assetInventory.buildingsOnForeignLandDetails": "Bauten auf fremden Grundstücken",
  "assetInventory.hasOtherItems": "Sonstige Sachen vorhanden",
  "assetInventory.otherItemsDetails": "Sonstige Sachen",
  "assetInventory.ownsAllItems": "Alle Sachen gehören der Kund:in",
  "assetInventory.itemsOwnedByOthersDetails": "Sachen, die nicht der Kund:in gehören",
  "assetFinancialHolding.kind": "Art",
  "assetFinancialHolding.institutionName": "Name der Bank/Versicherung",
  "assetFinancialHolding.accountNumber": "Konto- bzw. Polizzennummer",
  "assetFinancialHolding.street": "Straße",
  "assetFinancialHolding.houseNumber": "Hausnummer",
  "assetFinancialHolding.zipCode": "PLZ",
  "assetFinancialHolding.city": "Ort",
  "assetFinancialHolding.country": "Land",
  "assetFinancialHolding.encumbrance": "Vinkuliert, abgetreten oder verpfändet",
  "assetFinancialHolding.encumberedClaimId": "Gesicherte Forderung",
  "assetFinancialHolding.usedForProceedingCosts": "Zur Ansparung der Verfahrenskosten verwendet",
  "assetFinancialHolding.isValueKnown": "Aktueller Wert bekannt",
  "assetFinancialHolding.value": "Höhe des aktuellen Wertes",
  "assetFinancialHolding.valueDate": "Stichtag des Wertes",
  "assetFinancialHolding.remarks": "Weitere Angaben",
  "assetInventory.hasTenancyRights": "Mietrechte vorhanden",
  "assetInventory.tenantOrOwnerOfHomeAddress": "Mieter bzw. Eigentümer der Wohnadresse",
  "assetInventory.hasCompanyShares": "Gesellschaftsbeteiligung vorhanden",
  "assetInventory.hasCooperativeMemberships": "Genossenschaftsmitgliedschaft vorhanden",
  "assetInventory.hasOtherPropertyRights": "Sonstige Vermögensrechte vorhanden",
  "assetInventory.hasGratuitousDispositions": "Unentgeltliche Verfügungen zugunsten Angehöriger",
  "assetInventory.gratuitousDispositionsDetails": "Angaben zu den unentgeltlichen Verfügungen",
  "assetInventory.hasOtherDispositions": "Sonstige Verfügungen zugunsten Angehöriger",
  "assetInventory.otherDispositionsDetails": "Angaben zu den sonstigen Verfügungen",
  "assetInventory.hasAssetSettlement": "Vermögensauseinandersetzung mit Angehörigen",
  "assetInventory.assetSettlementDetails": "Angaben zur Vermögensauseinandersetzung",
  "assetPropertyRight.kind": "Art",
  "assetPropertyRight.landlord": "Vermieter",
  "assetPropertyRight.isCurrentHome": "Aktuell bewohnte Wohnung",
  "assetPropertyRight.propertyDetails": "Angaben zum Mietobjekt",
  "assetPropertyRight.expectsProceeds": "Erlös bei Auszug zu erwarten",
  "assetPropertyRight.proceedsEncumbrance": "Erlös verpfändet",
  "assetPropertyRight.encumberedClaimId": "Pfandgläubiger",
  "assetPropertyRight.isProceedsAmountKnown": "Höhe des Erlöses bekannt",
  "assetPropertyRight.proceedsAmount": "Höhe des Erlöses",
  "assetPropertyRight.proceedsDate": "Stichtag des Erlöses",
  "assetPropertyRight.remarks": "Zusätzliche Bemerkungen",
  "assetPropertyRight.details": "Nähere Angaben",
  "assetExpense.position": "Ausgabe",
  "assetExpense.amount": "Betrag",
  "assetExpense.debtorShare": "Anteil des Schuldners",
  "assetOwnClaim.debtorName": "Name des Schuldners",
  "assetOwnClaim.debtorAddress": "Anschrift des Schuldners",
  "assetOwnClaim.isRelated": "Schuldner verwandt",
  "assetOwnClaim.relationship": "Verwandtschaftsverhältnis",
  "assetOwnClaim.legalReason": "Rechtsgrund",
  "assetOwnClaim.amount": "Höhe der Forderung",
  "assetOwnClaim.dueDate": "Fälligkeitsdatum",
  "assetOwnClaim.isSecured": "Forderung besichert",
  "assetOwnClaim.securities": "Sicherheiten",
  "assetOwnClaim.isDisputed": "Forderung streitig",
  "assetOwnClaim.hasPendingCourtProceedings": "Gerichtsverfahren anhängig",
  "assetOwnClaim.disputeDetails": "Angaben zur Streitigkeit",
  "assetOwnClaim.isCollectible": "Forderung einbringlich",
  "assetOwnClaim.collectionSteps": "Schritte zur Einbringlichmachung",
  "assetAccount.bankName": "Bank",
  "assetAccount.bankAddress": "Adresse der Bank",
  "assetAccount.iban": "IBAN",
  "assetAccount.incomings": "Zahlungseingänge",
  "assetAccount.isOverdrawn": "Konto überzogen",
  "assetDependant.firstName": "Vorname",
  "assetDependant.relationship": "Beziehung",
  "assetDependant.birthDate": "Geburtsdatum",
  "assetDependant.livesInHousehold": "Lebt im gemeinsamen Haushalt",
  "assetDependant.isJointlyLiable": "Mithaftung",
  "assetDependant.isEntitledToMaintenance": "Unterhaltsberechtigt",
  "assetDependant.hasToPayAlimony": "Unterhaltszahlung",
  "assetDependant.monthlyAlimonyAmount": "Monatlicher Unterhalt",
  "assetDependant.paidFullLastMonth": "Letztes Monat voll bezahlt",
  "assetDependant.fullyGarnishedLastMonth": "Letztes Monat voll gepfändet",
  "assetDependant.note": "Anmerkung",
  "assetIncome.payerNameAndAddress": "Bezugsauszahlende Stelle",
  "assetIncome.incomeType": "Einkommensart",
  "assetIncome.hasSpecialPayments": "Anspruch auf Sonderzahlungen",
  "assetIncome.specialPaymentCount": "Anzahl der Sonderzahlungen",
  "assetIncome.dailyRate": "Tagsatz",
  "assetIncome.note": "Anmerkung",
  "assetIncome.isPartiallyGarnished": "Zum Teil gepfändet",
  "assetIncome.hasEmployerFamilyAllowance": "Familienbeihilfe durch Dienstgeber",
  "assetIncome.hasEmployerSettledExpenses": "Persönliche Ausgaben über Dienstgeber",
  "assetIncome.hasBenefitInKind": "Naturalleistung",
  "assetIncome.benefitInKindAmount": "Wert des Naturalbezuges",
  "assetIncome.receivesTips": "Trinkgelder",
  "assetIncome.monthlyTipAmount": "Monatliches Trinkgeld",
  "assetIncome.hasExpenseAllowances": "Aufwandsentschädigungen",
  "assetIncome.isAverageRepresentative": "Durchschnittseinkommen charakteristisch",
  "assetIncome.nonRepresentativeReason": "Begründung nicht charakteristisch",
  "assetIncome.calculatedAverageIncome": "Durchschnittseinkommen",
  "assetIncome.actualAverageIncome": "Tatsächliches Durchschnittseinkommen",
  "claim.creditorId": "Gläubiger:in",
  "claim.representativeId": "Vertretung",
  "claim.caseNumber": "Aktenzeichen",
  "claim.legalBasis": "Rechtsgrund",
  "claim.disputedStatus": "Bestritten",
  "claim.collateralStatus": "Besichert",
  "claim.expectedLossPercent": "Vermutlicher Ausfall (%)",
  "claim.claimEntirelyDisputed": "Zur Gänze bestritten",
  "claim.recognizedAmount": "Anerkannter Betrag",
  "claim.srvRegistrationStatus": "Im SRV angemeldet",
  "claim.srvOrderNumber": "Ordnungsnummer",
  "claim.notes": "Anmerkungen",
  "claim.isCompleted": "Erledigt",
  "balanceEntry.date": "Datum",
  "balanceEntry.amount": "Betrag",
  "balanceEntry.entryType": "Art",
  "claimCollateral.type": "Art der Sicherheit",
  "claimCollateral.holdingId": "Vermögenswert",
  "claimCollateral.details": "Nähere Angaben",
  "bankruptcyChecklist.hasRentArrears": "Mietrückstand",
  "bankruptcyChecklist.hasUtilityArrears": "Betriebskostenrückstand",
  "bankruptcyChecklist.hasOpenPenalties": "Offene Strafen",
  "bankruptcyChecklist.hasInstallmentAgreement": "Ratenvereinbarung",
  "bankruptcyChecklist.canMeetInstallmentAgreement": "Ratenvereinbarung einhaltbar",
  "bankruptcyChecklist.hasChildrenWithMaintenance": "Kinder mit Unterhalt",
  "bankruptcyChecklist.paysFullMaintenance": "Voller Unterhalt wird bezahlt",
  "bankruptcyChecklist.isMaintenanceGarnished": "Unterhalt wird gepfändet",
  "bankruptcyChecklist.wasPreviouslySelfEmployed": "Frühere Selbstständigkeit",
  "bankruptcyChecklist.isCurrentlySelfEmployed": "Aktuelle Selbstständigkeit",
  "bankruptcyChecklist.hasBusinessLicense": "Aktueller Gewerbeschein",
  "bankruptcyChecklist.exercisesTradeAsBusiness": "Als Gewerbe ausgeübt",
  "bankruptcyChecklist.isBusinessLicenseDormant": "Gewerbe ruhend",
  "bankruptcyChecklist.hasMaintenanceArrears": "Unterhaltsrückstände",
  "bankruptcyChecklist.hasCriminalDebts": "Deliktschulden",
  "bankruptcyChecklist.hasCreditDebts": "Kreditschulden",
  "bankruptcyChecklist.creditWithinLastFiveYears": "Kredit innerhalb der letzten 5 Jahre",
  "bankruptcyChecklist.creditRemark": "Bemerkung der Schuldnerberatung (Kredit)",
  "bankruptcyChecklist.hasMailOrderDebts": "Versandhausschulden",
  "bankruptcyChecklist.mailOrderWithinLastThreeYears": "Versandhauskauf innerhalb der letzten 3 Jahre",
  "bankruptcyChecklist.mailOrderRemark": "Bemerkung der Schuldnerberatung (Versandhaus)",
  "bankruptcyChecklist.hasNeverPaidDebts": "Nie gezahlte Schulden",
  "bankruptcyChecklist.hasOwnCheckingAccount": "Eigenes Girokonto",
  "bankruptcyChecklist.isCheckingAccountOverdrawn": "Konto überzogen",
  "bankruptcyChecklist.hasRegisteredMobilePhone": "Mobiltelefon angemeldet",
  "bankruptcyChecklist.isElectricityInOwnName": "Strom auf eigenen Namen",
  "bankruptcyChecklist.isApartmentDistrictHeated": "Wohnung mit Fernwärme",
  "bankruptcyChecklist.isDistrictHeatingInOwnName": "Fernwärme auf eigenen Namen",
  "srvProceeding.applicationDate": "Antrag am",
  "srvProceeding.counselingType": "Beratungsart",
  "srvProceeding.openingDate": "Eröffnung am",
  "srvProceeding.annulmentDate": "Aufhebungsdatum",
  "srvProceeding.courtId": "Gericht",
  "srvProceeding.fileNumber": "Aktenzahl",
  "srvProceeding.trusteeId": "Treuhänder",
  "srvProceeding.insolvencyAdministratorId": "Masseverwalter",
  "srvProceeding.offeredQuota": "Quote angeboten",
  "srvProceeding.acceptedQuota": "Quote angenommen",
  "srvProceeding.proceedingCosts": "Verfahrenskosten",
  "srvProceeding.remark": "Bemerkung",
  "srvProceedingCourseEntry.dateUtc": "Datum",
  "srvProceedingCourseEntry.entryType": "Typ",
  "srvProceedingCourseEntry.result": "Ergebnis",
  "srvProceedingCourseEntry.remark": "Bemerkung",
  "contactDetail.type": "Art",
  "contactDetail.value": "Wert",
  "contactDetail.comment": "Bemerkung",
  // Per-type labels for the value of a classified entry (see changeHistoryFieldLabels' doc above):
  // what a contact detail's value is called depends on what kind of contact detail it is.
  "contactDetail.value.Email": "E-Mail",
  "contactDetail.value.Phone": "Nummer",
  "goal.goalResult": "Ziel/Ergebnis",
  "goal.achievementDate": "Datum",
  "debt.type": "Art",
  "debt.value": "Wert",
  "creditor.name": "Name",
  "creditor.creditorType": "Gläubigertyp",
  "creditor.type": "Typ",
  "creditor.street": "Straße",
  "creditor.houseNumber": "Hausnummer",
  "creditor.country": "Land",
  "creditor.zipCode": "Plz",
  "creditor.city": "Ort",
  "creditor.note": "Anmerkung",
  "creditor.isActive": "Aktiv",
  "creditor.deactivationReason": "Deaktivierungsgrund",
  "creditor.deactivatedAtUtc": "Deaktiviert am",
};

// Collection entities render their label in the first column ("Feld"); the one-per-client aggregates
// (client, assetInventory, bankruptcyChecklist) are named in their creation row's text instead
// ("Vermögensverzeichnis angelegt"), so a section fed by several aggregates can tell the
// creations apart.
export const changeHistoryEntityLabels: Record<string, string> = {
  client: "Kund:in",
  assetInventory: "Vermögensverzeichnis",
  bankruptcyChecklist: "Konkurs Checkliste",
  contactDetail: "Kontaktdaten",
  goal: "Ziel/Ergebnis",
  debt: "Massnahme/Ursache",
  caseNote: "Aktenvermerk",
  assetAccount: "Konto",
  assetDependant: "Angehörige:r",
  assetIncome: "Einkommen",
  assetExpense: "Ausgabe",
  assetOwnClaim: "Eigene Forderung",
  assetFinancialHolding: "Bank- oder Versicherungsvermögen",
  assetPropertyRight: "Anderes Vermögensrecht",
  claim: "Forderung",
  balanceEntry: "Saldenliste-Eintrag",
  claimCollateral: "Sicherheit",
  creditor: "Gläubiger:in",
  srvProceeding: "Verfahren",
  srvProceedingCourseEntry: "Verfahrensverlauf-Eintrag",
};

// Values of fields whose set is fixed in the backend, keyed "<fieldKey>.<value>". The value is an
// enum member name or the string a closed value object wraps — either way the backend sends it raw
// and the German word is chosen here.
export const changeHistoryFixedSetValueLabels: Record<string, string> = {
  "contactDetail.type.Email": "E-Mail",
  "contactDetail.type.Phone": "Telefonnummer",
  "debt.type.Measure": "Massnahme",
  "debt.type.Cause": "Ursache",
  "assetIncome.specialPaymentCount.One": "1 Sonderzahlung",
  "assetIncome.specialPaymentCount.Two": "2 Sonderzahlungen",
  "assetIncome.specialPaymentCount.ThreeOrMore": "3 oder mehr Sonderzahlungen",
  "assetExpense.position.Rent": "Miete (inkl. Betriebskosten)",
  "assetExpense.position.HomeOwnership": "Eigenheim",
  "assetExpense.position.Energy": "Strom/Gas",
  "assetExpense.position.Heating": "Heizung",
  "assetExpense.position.Phone": "Telefon",
  "assetExpense.position.Broadcasting": "ORF, Kabelfernsehen",
  "assetExpense.position.Childcare": "Kindergarten/Hort/Schule",
  "assetExpense.position.PublicTransport": "Öffentl. Verkehrsmittel (Netzkarte)",
  "assetExpense.position.HouseholdInsurance": "Haushaltsversicherung",
  "assetExpense.position.LifeInsurance": "Lebensversicherung",
  "assetExpense.position.OtherInsurance": "Sonstige Versicherungen",
  "assetExpense.position.Vehicles": "Kraftfahrzeuge",
  "assetExpense.position.VehicleInsuranceAndTax": "KFZ-Versicherung und Steuer",
  "assetExpense.position.Fuel": "Treibstoff",
  "assetExpense.position.Parking": "Autoabstellplatz (Miete)",
  "assetExpense.position.LivingCosts": "Lebensunterhalt",
  "assetExpense.position.Other": "Sonstiges",
  "assetFinancialHolding.kind.BuildingSavingsContract": "Bausparvertrag",
  "assetFinancialHolding.kind.SavingsAccount": "Sparbuch/-konto",
  "assetFinancialHolding.kind.LifeInsurance": "Lebensversicherung",
  "assetFinancialHolding.kind.PensionInsurance": "Pensionsvorsorgeversicherung",
  "assetFinancialHolding.kind.SecuritiesDepot": "Wertpapierdepot",
  "claimCollateral.type.BuildingSavingsContract": "Bausparvertrag",
  "claimCollateral.type.LifeInsurance": "Lebensversicherung",
  "claimCollateral.type.PensionInsurance": "Pensionsvorsorgeversicherung",
  "claimCollateral.type.SavingsAccount": "Sparbuch",
  "claimCollateral.type.Securities": "Wertpapiere",
  "claimCollateral.type.RealEstate": "Immobilie (Haus, Eigentumswohnung, Grundbesitz etc.)",
  "claimCollateral.type.WagePledge": "vertragliches Pfandrecht am Lohn/Gehalt",
  "claimCollateral.type.Other": "anderes",
  "assetFinancialHolding.encumbrance.Yes": "Ja",
  "assetFinancialHolding.encumbrance.No": "Nein",
  "assetFinancialHolding.encumbrance.Unknown": "Weiß nicht",
  "assetPropertyRight.kind.Tenancy": "Mietrecht",
  "assetPropertyRight.kind.CompanyShare": "Gesellschaftsbeteiligung",
  "assetPropertyRight.kind.CooperativeMembership": "Genossenschaftsmitgliedschaft",
  "assetPropertyRight.kind.Other": "Sonstiges Vermögensrecht",
  "assetPropertyRight.proceedsEncumbrance.Yes": "Ja",
  "assetPropertyRight.proceedsEncumbrance.No": "Nein",
  "assetPropertyRight.proceedsEncumbrance.Unknown": "Weiß nicht",
  // The four claim fields store the ClaimStatus values; they are not a CLR enum, but the change
  // history treats them the same way — the value travels raw and the label is resolved here.
  "caseNote.appointmentStatus.ATTENDED": "wahrgenommen",
  "caseNote.appointmentStatus.CANCELLED_OR_RESCHEDULED": "abgesagt/verschoben",
  "caseNote.appointmentStatus.NO_SHOW": "nicht erschienen",
  "caseNote.appointmentStatus.RESCHEDULED_BY_STAFF": "von SB verschoben",
  "claim.disputedStatus.JA": "Ja",
  "claim.disputedStatus.NEIN": "Nein",
  "claim.disputedStatus.UNBEKANNT": "Unbekannt",
  "claim.collateralStatus.JA": "Ja",
  "claim.collateralStatus.NEIN": "Nein",
  "claim.collateralStatus.UNBEKANNT": "Weiß nicht",
  "claim.claimEntirelyDisputed.JA": "Ja",
  "claim.claimEntirelyDisputed.NEIN": "Nein",
  "claim.claimEntirelyDisputed.UNBEKANNT": "Unbekannt",
  "claim.srvRegistrationStatus.JA": "Ja",
  "claim.srvRegistrationStatus.NEIN": "Nein",
  "claim.srvRegistrationStatus.UNBEKANNT": "Unbekannt",
};
